import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Zap, Send, Loader2, User, MessageCircle, Check, Mic, MicOff, Volume2, VolumeX, ShieldCheck, ShieldX } from "lucide-react";
import { useVoiceChat } from "@/hooks/useVoiceChat";
import RetellCallButton from "@/components/retell-call-button";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface TalkMessage {
  role: "user" | "assistant";
  content: string;
  actions?: Array<{ tool: string; ok: boolean }>;
}

interface CredentialApproval {
  id: string;
  recordName: string;
  taskSummary: string;
}

const TOOL_LABELS: Record<string, string> = {
  create_task: "Created task",
  update_task: "Updated task",
  create_goal: "Created goal",
  update_goal: "Updated goal",
  create_habit: "Created habit",
  create_journal_entry: "Created journal entry",
  create_note: "Created note",
  create_calendar_event: "Created calendar event",
  create_zeus_rule: "Created Zeus rule",
  get_goals: "Checked goals",
  get_habits: "Checked habits",
  get_tasks: "Checked tasks",
  get_journal_entries: "Checked journal",
  get_tracker_data: "Checked trackers",
  get_calendar_events: "Checked calendar",
  get_recent_activity: "Reviewed recent activity",
  search_content: "Searched content",
  get_google_calendar_events: "Checked Google Calendar",
};

const TOOL_INVALIDATIONS: Record<string, string[]> = {
  create_task: ["/api/tasks"],
  update_task: ["/api/tasks"],
  create_goal: ["/api/goals"],
  update_goal: ["/api/goals"],
  create_habit: ["/api/habits"],
  create_journal_entry: ["/api/journal-entries"],
  create_note: ["/api/notes"],
  create_calendar_event: ["/api/calendar-events"],
  create_zeus_rule: ["/api/zeus/rules"],
};

export default function TalkToZeus() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<TalkMessage[]>([]);
  const [input, setInput] = useState("");
  const [pendingApprovals, setPendingApprovals] = useState<CredentialApproval[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const speakNextRef = useRef(false);

  const voice = useVoiceChat({
    onTranscript: (text) => {
      setInput("");
      sendText(text, true);
    },
    onInterim: (text) => setInput(text),
    onMicError: (message) => {
      toast({ title: "Microphone problem", description: message, variant: "destructive" });
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (!open) {
      voice.stopListening();
      voice.stopSpeaking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const talkMutation = useMutation({
    mutationFn: async (message: string) => {
      const res = await apiRequest("POST", "/api/zeus/talk", {
        message,
        conversationHistory: messages.map((m) => ({ role: m.role, content: m.content })),
      });
      return (await res.json()) as {
        response: string;
        toolCallsMade: Array<{ tool: string; ok: boolean }>;
        pendingCredentialApprovals?: CredentialApproval[];
      };
    },
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response, actions: data.toolCallsMade },
      ]);
      setPendingApprovals(data.pendingCredentialApprovals ?? []);
      if (speakNextRef.current || voice.voiceReplies) {
        voice.speakText(data.response);
      }
      speakNextRef.current = false;
      const invalidated = new Set<string>();
      for (const call of data.toolCallsMade ?? []) {
        if (!call.ok) continue;
        for (const key of TOOL_INVALIDATIONS[call.tool] ?? []) {
          if (!invalidated.has(key)) {
            invalidated.add(key);
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }
      }
    },
    onError: (err: Error) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again." },
      ]);
      speakNextRef.current = false;
      // Don't keep the mic loop running into an error state.
      voice.stopListening();
      toast({ title: "Zeus is unavailable", description: err.message, variant: "destructive" });
    },
  });

  const resolveApproval = async (approval: CredentialApproval, decision: "approve" | "deny") => {
    try {
      await apiRequest("POST", `/api/zeus/credential-approvals/${approval.id}/${decision}`, {});
      setPendingApprovals((prev) => prev.filter((a) => a.id !== approval.id));
      if (decision === "approve") {
        // Tell Zeus so it retries the launch with the now-approved id.
        sendText(
          `I approved using my ${approval.recordName} login (approval id ${approval.id}). Launch the task now by calling launch_manus_task again with approvalId "${approval.id}" and the exact same prompt as before.`,
        );
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Okay — I won't use your ${approval.recordName} login. The task was not launched.` },
        ]);
      }
    } catch {
      toast({ title: "Approval failed", description: "The approval may have expired. Ask Zeus to try again.", variant: "destructive" });
      setPendingApprovals((prev) => prev.filter((a) => a.id !== approval.id));
    }
  };

  const sendText = (raw: string, fromVoice = false) => {
    const text = raw.trim();
    if (!text || talkMutation.isPending) return;
    speakNextRef.current = fromVoice;
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    talkMutation.mutate(text);
  };

  const send = () => sendText(input);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="fixed bottom-4 right-4 z-40 gap-2 rounded-full bg-gradient-to-r from-yellow-500 to-amber-600 px-4 text-white shadow-lg shadow-yellow-500/25 hover:from-yellow-600 hover:to-amber-700 md:static md:z-auto md:rounded-md md:px-4 md:shadow-none"
          data-testid="button-talk-to-zeus"
        >
          <MessageCircle className="h-4 w-4" /> Talk to Zeus
        </Button>
      </DialogTrigger>
      <DialogContent
        className="h-[100dvh] !max-h-[100dvh] !overflow-hidden flex flex-col gap-0 p-0"
        data-testid="dialog-talk-to-zeus"
      >
        <DialogHeader className="shrink-0 border-b border-border/60 px-4 py-3 sm:px-6 sm:py-4">
          <DialogTitle className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-yellow-400 to-amber-600 shadow-md shadow-yellow-500/20">
              <Zap className="h-5 w-5 text-white" style={{ fill: "white" }} />
            </div>
            Talk to Zeus
            <div className="ml-auto mr-8 flex items-center gap-2">
              {voice.ttsSupported && (
                <Button
                  variant={voice.voiceReplies ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8"
                  title={voice.voiceReplies ? "Voice replies on — Zeus reads answers aloud" : "Voice replies off"}
                  onClick={() => {
                    if (voice.speaking) voice.stopSpeaking();
                    voice.setVoiceReplies(!voice.voiceReplies);
                  }}
                  data-testid="button-zeus-voice-replies"
                >
                  {voice.voiceReplies ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </Button>
              )}
              <RetellCallButton />
            </div>
          </DialogTitle>
          <DialogDescription>
            Ask Zeus anything, or tell him what to do — he can create and update your goals, tasks, habits, journal entries, calendar events, and automation rules.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground">
              <Zap className="h-10 w-10 text-yellow-500/60" />
              <p className="text-sm font-medium">What can Zeus do for you?</p>
              <div className="flex max-w-lg flex-wrap justify-center gap-2">
                {[
                  "Add a task to call the dentist tomorrow",
                  "How am I doing on my goals?",
                  "Create a habit to read 20 minutes daily",
                  "Every Monday morning, summarize my week ahead",
                ].map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="rounded-full border border-border/60 px-3 py-1.5 text-xs transition-colors hover:bg-accent"
                    data-testid="button-zeus-suggestion"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-3xl space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                  {m.role === "assistant" && (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-yellow-400 to-amber-600">
                      <Zap className="h-4 w-4 text-white" style={{ fill: "white" }} />
                    </div>
                  )}
                  <div className={`max-w-[80%] space-y-2 ${m.role === "user" ? "order-first" : ""}`}>
                    <div
                      className={`whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${
                        m.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      {m.content}
                    </div>
                    {m.actions && m.actions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {m.actions.map((a, j) => (
                          <Badge key={j} variant={a.ok ? "secondary" : "destructive"} className="gap-1 text-[10px]">
                            {a.ok ? <Check className="h-3 w-3" /> : null}
                            {TOOL_LABELS[a.tool] ?? a.tool}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  {m.role === "user" && (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <User className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}
              {pendingApprovals.map((a) => (
                <div
                  key={a.id}
                  className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
                  data-testid={`card-credential-approval-${a.id}`}
                >
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <ShieldCheck className="h-4 w-4 text-amber-600" />
                    Allow Zeus to use your "{a.recordName}" login?
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    A secure agent will log in once to complete this task: {a.taskSummary}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Your password is never shown to the AI or included in the task — it's handed off once through a secure, single-use channel.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      className="gap-1.5"
                      onClick={() => resolveApproval(a, "approve")}
                      data-testid={`button-approve-credential-${a.id}`}
                    >
                      <ShieldCheck className="h-3.5 w-3.5" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => resolveApproval(a, "deny")}
                      data-testid={`button-deny-credential-${a.id}`}
                    >
                      <ShieldX className="h-3.5 w-3.5" /> Deny
                    </Button>
                  </div>
                </div>
              ))}
              {talkMutation.isPending && (
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-yellow-400 to-amber-600">
                    <Zap className="h-4 w-4 text-white" style={{ fill: "white" }} />
                  </div>
                  <div className="flex items-center gap-2 rounded-2xl bg-muted px-4 py-2.5 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Zeus is working…
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-border/60 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-4 sm:pb-4">
          <div className="mx-auto flex w-full max-w-3xl items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={voice.listening ? "Listening…" : "Tell Zeus what to do…"}
              rows={2}
              className="resize-none"
              data-testid="input-talk-to-zeus"
            />
            {voice.sttSupported && (
              <Button
                onClick={voice.toggleListening}
                size="icon"
                variant={voice.listening || voice.conversationMode ? "destructive" : "outline"}
                className="h-10 w-10 shrink-0"
                title={
                  voice.listening || voice.conversationMode
                    ? "End voice conversation"
                    : "Start voice conversation with Zeus"
                }
                data-testid="button-zeus-mic"
              >
                {voice.listening || voice.conversationMode ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
            )}
            <Button
              onClick={send}
              disabled={!input.trim() || talkMutation.isPending}
              size="icon"
              className="h-10 w-10 shrink-0 bg-gradient-to-r from-yellow-500 to-amber-600 text-white hover:from-yellow-600 hover:to-amber-700"
              data-testid="button-send-zeus"
            >
              {talkMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
